/* global console, document, window, Excel, Word, Office, crypto */

import { PublicClientApplication, createNestablePublicClientApplication } from "@azure/msal-browser";
import * as XLSX from "xlsx";
import {
  msalConfig,
  graphScopes,
  oneDriveScopes,
  backendUrl,
  PERSONAL_ACCOUNT_TENANT_ID,
  MICROSOFT_CONSUMER_MAILBOX_DOMAIN,
} from "../authConfig";
import { evaluateRecipientQuota } from "./quota";

// Created in ensureSignedIn() -- either a nestable client (nested app authentication, shares
// the identity Office itself is signed into across host apps) or a classic one (fallback for
// older Office builds that don't support NAA yet). Both expose the same MSAL API surface
// used below.
let msalInstance = null;
let currentSessionId = null;
let sessionList = [];
let insertTarget = "document"; // "document" | "subject" | "body" | "filename"

// Report tab -- paginated view over batch summaries (one per /run or /relaunch call, across
// all sessions, most recent first). Each card fetches its own row-level detail on demand
// (download / relaunch), so nothing large is held in memory here.
let runSummaries = [];
let runsCursor = "";
let runsLoading = false;

// Status updates surface as a transient notification (10s) rather than a persistent log --
// the Report tab (backed by the Audit table) is the durable record of what happened.
function log(message, kind) {
  showToast(message, kind);
}

let toastTimer = null;
function showToast(message, kind) {
  const toast = document.getElementById("toast");
  // Only the message text swaps out -- the close button is a permanent child of #toast, not
  // rebuilt on every call.
  document.getElementById("toast-message").textContent = message;
  toast.className = kind === "error" ? "toast error visible" : "toast visible";
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => toast.classList.remove("visible"), 10000);
}

function dismissToast() {
  clearTimeout(toastTimer);
  document.getElementById("toast").classList.remove("visible");
}

function setRunProgress(done, total) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  document.getElementById("run-progress").style.display = "block";
  document.getElementById("run-progress-fill").style.width = `${pct}%`;
  document.getElementById("run-progress-label").textContent = `Sending ${done} of ${total} (${pct}%)`;
}

function hideRunProgress() {
  document.getElementById("run-progress").style.display = "none";
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Expanded by default; auto-collapsed once data loads successfully (see connectExcelData)
 *  so the "Connected" summary isn't pushed below the fold. `force` (true/false) sets the
 *  state directly; omitted, it just flips whatever it currently is. */
function toggleValidationChecklist(force) {
  const toggle = document.getElementById("validation-toggle");
  const open = force !== undefined ? force : !toggle.classList.contains("open");
  toggle.classList.toggle("open", open);
  document.getElementById("validation-checklist").style.display = open ? "flex" : "none";
}

// Which box a clicked tag lands in is inferred, not chosen from a button: goToStep() forces
// "document" while on the document step, and forces "subject" on entry to the compose step,
// after which the focus listeners below (on email-subject/email-body-editor/filename-input)
// take over as the user clicks between those three fields.
function setInsertTarget(target) {
  insertTarget = target;
}

function getInitials(account) {
  const source = account.name || account.username || "?";
  const parts = source.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Cc/Bcc boxes hold a comma/semicolon-separated list of addresses, applied as-is to every
 *  email in the batch (unlike the per-row "Email" column, these aren't merge-templated). */
function parseEmailList(raw) {
  return raw
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter(Boolean);
}

/** Returns the entries that don't look like an email address, or [] if the field is empty or
 *  every entry is valid. Used to block sending on a typo'd Cc/Bcc address rather than letting
 *  Graph reject the whole message mid-batch. */
function invalidEmails(raw) {
  return parseEmailList(raw).filter((e) => !EMAIL_PATTERN.test(e));
}

/**
 * If exactly one column matches "email" case-insensitively but isn't spelled exactly
 * "Email", rename it to "Email" in place so the rest of the pipeline (which expects that
 * exact key) works regardless of how the user capitalized their header. If more than one
 * column matches (e.g. both "Email" and "email"), leave them alone -- the duplicate-header
 * check below will correctly flag that as ambiguous instead of silently picking one.
 */
function canonicalizeEmailColumn(columns, rows) {
  const matches = columns.filter((c) => c.trim().toLowerCase() === "email");
  if (matches.length !== 1 || matches[0] === "Email") return;

  const original = matches[0];
  columns[columns.indexOf(original)] = "Email";
  rows.forEach((row) => {
    row["Email"] = row[original];
    delete row[original];
  });
}

function readValidationOptions() {
  return {
    checkEmailFormat: document.getElementById("check-email-format").checked,
    checkAllNonEmpty: document.getElementById("check-all-nonempty").checked,
    checkBlankHeaders: document.getElementById("check-blank-headers").checked,
    checkDupEmails: document.getElementById("check-dup-emails").checked,
  };
}

/** Client-side checks before a session is even created: catches typos early instead of
 *  letting them silently fail Graph sendMail one row at a time during the batch run.
 *  Duplicate headers and Email-non-empty are always enforced (locked in the UI) since
 *  skipping them doesn't skip a nice-to-have, it lets silent data loss through. */
function validateRows(columns, rows, options) {
  const errors = [];

  const seen = new Map();
  columns.forEach((col) => {
    const key = col.trim().toLowerCase();
    seen.set(key, (seen.get(key) || 0) + 1);
  });
  const duplicates = [...seen.entries()].filter(([, count]) => count > 1).map(([key]) => key);
  if (duplicates.length > 0) {
    errors.push(`Duplicate column headers (case-insensitive): ${duplicates.join(", ")}. Rename them so each column is unique.`);
  }

  if (options.checkBlankHeaders) {
    const blankCount = columns.filter((c) => !c.trim()).length;
    if (blankCount > 0) errors.push(`${blankCount} column header(s) are blank. Give every column a name.`);
  }

  const badRows = [];
  rows.forEach((row, i) => {
    const email = (row["Email"] || "").trim();
    if (!email) {
      badRows.push(`row ${i + 1} is empty`);
    } else if (options.checkEmailFormat && !EMAIL_PATTERN.test(email)) {
      badRows.push(`row ${i + 1} ("${email}")`);
    }
  });
  if (badRows.length > 0) {
    const preview = badRows.slice(0, 5).join(", ");
    const more = badRows.length > 5 ? ` and ${badRows.length - 5} more` : "";
    errors.push(`Email column has problems: ${preview}${more}.`);
  }

  if (options.checkAllNonEmpty) {
    const blankCells = [];
    rows.forEach((row, i) => {
      columns.forEach((col) => {
        if (col !== "Email" && !String(row[col] ?? "").trim()) blankCells.push(`row ${i + 1} / "${col}"`);
      });
    });
    if (blankCells.length > 0) {
      const preview = blankCells.slice(0, 5).join(", ");
      const more = blankCells.length > 5 ? ` and ${blankCells.length - 5} more` : "";
      errors.push(`Blank cells found: ${preview}${more}.`);
    }
  }

  if (options.checkDupEmails) {
    const counts = new Map();
    rows.forEach((row) => {
      const email = (row["Email"] || "").trim().toLowerCase();
      if (email) counts.set(email, (counts.get(email) || 0) + 1);
    });
    const dupEmails = [...counts.entries()].filter(([, count]) => count > 1).map(([email]) => email);
    if (dupEmails.length > 0) {
      errors.push(`Duplicate email addresses across rows: ${dupEmails.join(", ")}.`);
    }
  }

  return errors;
}

/** Compose step's Next button unlocks once every field is filled except the optional archive
 *  folder. */
function updateComposeNextState() {
  const requiredIds = ["email-subject", "filename-input"];
  const allFilled =
    requiredIds.every((id) => document.getElementById(id).value.trim().length > 0) &&
    document.getElementById("email-body-editor").textContent.trim().length > 0;
  document.getElementById("step3-next").disabled = !allFilled;
}

function timeAgo(timestamp) {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const WELCOME_SEEN_KEY = "truemerge_welcome_seen";

/** First-run intro, shown before sign-in so the value proposition is clear up front.
 *  Resolves once the user clicks "Get started" (immediately if already seen). */
function showWelcomeOnce() {
  let seen = false;
  try { seen = localStorage.getItem(WELCOME_SEEN_KEY) === "1"; } catch (e) { /* storage unavailable */ }
  if (seen) return Promise.resolve();

  const welcome = document.getElementById("welcome");
  welcome.style.display = "block";
  return new Promise((resolve) => {
    document.getElementById("welcome-start").onclick = () => {
      try { localStorage.setItem(WELCOME_SEEN_KEY, "1"); } catch (e) { /* ignore */ }
      welcome.style.display = "none";
      resolve();
    };
  });
}

Office.onReady(async (info) => {
  document.getElementById("sideload-msg").style.display = "none";
  await showWelcomeOnce();
  document.getElementById("auth-gate").style.display = "block";

  const signedIn = await ensureSignedIn();
  if (!signedIn) return;

  document.getElementById("auth-gate").style.display = "none";
  document.getElementById("app-content").style.display = "block";
  const account = msalInstance.getAllAccounts()[0];
  document.getElementById("account-name").textContent = account.username;
  document.getElementById("account-avatar").textContent = getInitials(account);
  document.getElementById("switch-account").onclick = toggleSwitchAccountInfo;
  document.getElementById("switch-account-dismiss").onclick = () => {
    document.getElementById("switch-account-info").style.display = "none";
  };
  document.getElementById("toast-close").onclick = dismissToast;

  if (info.host === Office.HostType.Excel) {
    document.getElementById("excel-section").style.display = "block";
    document.getElementById("connect-data").onclick = connectExcelData;
    document.getElementById("validation-toggle").onclick = () => toggleValidationChecklist();
  } else if (info.host === Office.HostType.Word) {
    document.getElementById("word-section").style.display = "block";
    document.getElementById("refresh-sessions").onclick = loadSessionList;
    document.getElementById("session-picker").onchange = onSessionPicked;
    document.getElementById("preview-row").onclick = previewRow;
    document.getElementById("run-batch").onclick = runBatch;
    document.getElementById("audit-load-more").onclick = () => loadRunSummaries(false);
    document.getElementById("email-subject").addEventListener("focus", () => setInsertTarget("subject"));
    document.getElementById("email-body-editor").addEventListener("focus", () => setInsertTarget("body"));
    document.getElementById("filename-input").addEventListener("focus", () => setInsertTarget("filename"));
    ["email-subject", "email-body-editor", "filename-input"].forEach((id) => {
      document.getElementById(id).addEventListener("input", updateComposeNextState);
    });
    setupRichTextToolbar();
    document.getElementById("tag-search-doc").addEventListener("input", (e) => filterTagPills(e.target, "tag-buttons-doc"));
    document.getElementById("tag-search-compose").addEventListener("input", (e) => filterTagPills(e.target, "tag-buttons-compose"));
    document.getElementById("step1-next").onclick = () => goToStep(2);
    document.getElementById("step2-back").onclick = () => goToStep(1);
    document.getElementById("step2-next").onclick = () => goToStep(3);
    document.getElementById("step3-back").onclick = () => goToStep(2);
    document.getElementById("step3-next").onclick = () => goToStep(4);
    document.getElementById("step4-back").onclick = () => goToStep(3);
    document.querySelectorAll("#word-tab-bar .tab-btn").forEach((btn) => {
      btn.addEventListener("click", () => switchTab(btn.dataset.tab));
    });
    goToStep(1);
    switchTab("actions");
    updateComposeNextState();
    await loadSessionList();
  }
});

// ===================== AUTH =====================

// True once signed in through nested app authentication (NAA) -- shares the identity Office
// itself is already signed into across host apps (Excel, Word), via ssoSilent/acquireTokenPopup
// on a *nestable* MSAL client. Falls back to a classic MSAL popup client for older Office
// builds that don't support NAA yet -- that fallback still prompts again per host app (MSAL's
// browser storage is siloed per host app's WebView2 instance), a known limitation of the
// fallback, not something this fixes.
let naaMode = false;

// A personal Microsoft account signed in with a non-Microsoft address (gmail.com, yahoo.com,
// ...) only has a hidden, often never-provisioned Outlook mailbox behind it -- Graph Mail.Send
// fails on those mid-batch. outlook.com/hotmail.com/live.com/msn.com logins *are* their own
// Microsoft-hosted mailbox, so they work fine despite also being personal accounts. See
// MICROSOFT_CONSUMER_MAILBOX_DOMAIN in authConfig.js.
function isUnsupportedAccount(account) {
  if (account?.tenantId !== PERSONAL_ACCOUNT_TENANT_ID) return false; // work/school -- always fine
  const domain = (account?.username ?? "").split("@")[1]?.toLowerCase() ?? "";
  return !MICROSOFT_CONSUMER_MAILBOX_DOMAIN.test(domain);
}

/** Replaces the auth-gate's "signing in" state with a blocking message for an account that
 *  signed in successfully but isn't a supported type -- either real switch-account instructions
 *  (NAA mode) or a working "Use a different account" button (classic MSAL popup fallback). */
function blockUnsupportedAccount(account) {
  document.getElementById("auth-heading").textContent = "Account not supported";
  document.getElementById("auth-status").textContent =
    `"${account.username}" can't reliably send email in bulk through Truemerge. Sign in with a ` +
    `work or school account, or a personal outlook.com/hotmail.com/live.com account instead.`;
  const retryBtn = document.getElementById("auth-retry");

  if (naaMode) {
    // NAA has no account picker of its own -- it mirrors whatever account Office itself is
    // already signed into for this host app, so a button here can't switch accounts, and
    // window.location.reload() doesn't reliably re-run NAA's sign-in either (the taskpane's
    // webview/NAA broker session can outlive a plain reload). No button to click at all --
    // closing and reopening the task pane is the one thing that actually re-initializes it.
    retryBtn.style.display = "none";
    document.getElementById("auth-switch-steps").style.display = "block";
  } else {
    // Classic MSAL popup client does have its own picker, so this button can actually switch
    // accounts on its own -- no manual Office-level steps needed.
    retryBtn.style.display = "inline-block";
    retryBtn.textContent = "Use a different account";
    retryBtn.onclick = async () => {
      try {
        const result = await msalInstance.loginPopup({ scopes: graphScopes, prompt: "select_account" });
        msalInstance.setActiveAccount(result.account);
        window.location.reload();
      } catch (error) {
        console.error(error);
      }
    };
  }
}

/** NAA needs a login hint to pick the right account when running in a browser context
 *  (Office on the web) -- harmless to always try, even for the desktop-only hosts this add-in
 *  currently targets. */
async function getLoginHint() {
  try {
    const authContext = await Office.auth.getAuthContext();
    return authContext?.userPrincipalName || undefined;
  } catch {
    return undefined;
  }
}

async function ensureSignedIn() {
  document.getElementById("auth-retry").onclick = () => window.location.reload();

  try {
    msalInstance = await createNestablePublicClientApplication(msalConfig);
    naaMode = true;
    await getGraphToken(graphScopes); // primes sign-in, prompting interactively if needed
    const account = msalInstance.getAllAccounts()[0];
    if (isUnsupportedAccount(account)) {
      blockUnsupportedAccount(account);
      return false;
    }
    return true;
  } catch (naaError) {
    console.warn("Nested app authentication unavailable, falling back to MSAL popup:", naaError.message);
    naaMode = false;
  }

  try {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    const existing = msalInstance.getAllAccounts()[0];
    let account;
    if (existing) {
      await msalInstance.acquireTokenSilent({ scopes: graphScopes, account: existing });
      account = existing;
    } else {
      const result = await msalInstance.loginPopup({ scopes: graphScopes, prompt: "select_account" });
      msalInstance.setActiveAccount(result.account);
      account = result.account;
    }
    if (isUnsupportedAccount(account)) {
      blockUnsupportedAccount(account);
      return false;
    }
    return true;
  } catch (error) {
    console.error(error);
    document.getElementById("auth-status").textContent = `Sign-in failed: ${error.message}`;
    document.getElementById("auth-retry").style.display = "inline-block";
    return false;
  }
}

async function getGraphToken(scopes = graphScopes) {
  if (naaMode) {
    const loginHint = await getLoginHint();
    try {
      const result = await msalInstance.ssoSilent({ scopes, loginHint });
      return result.accessToken;
    } catch (error) {
      // Docs say ssoSilent only throws InteractionRequiredAuthError when it needs a popup,
      // but in practice (no cached account yet, no login hint available) it can also surface
      // as a plain "user_canceled" error even though nothing was shown to the user. Retry via
      // popup for any silent failure here -- acquireTokenPopup is still NAA-broker-mediated
      // (see the MSAL NAA support table), so this stays in NAA mode instead of falling all
      // the way back to the classic per-host-storage path, which is what this exists to avoid.
      console.warn("ssoSilent failed, retrying interactively:", error.message);
      const result = await msalInstance.acquireTokenPopup({ scopes, loginHint });
      return result.accessToken;
    }
  }

  const account = msalInstance.getAllAccounts()[0];
  if (!account) throw new Error("Not signed in");
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes, account });
    return result.accessToken;
  } catch (error) {
    const result = await msalInstance.acquireTokenPopup({ scopes, account });
    return result.accessToken;
  }
}

async function authFetch(path, { scopes, ...options } = {}) {
  const accessToken = await getGraphToken(scopes);
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${accessToken}`,
    ...(options.headers || {}),
  };
  return fetch(`${backendUrl}${path}`, { ...options, headers });
}

/**
 * There's no supported "switch account" call under NAA -- the add-in doesn't hold its own
 * session, it's always whichever Microsoft account Office itself is currently signed into
 * (logout/logoutPopup/logoutRedirect all throw under NAA by design, and Microsoft's NAA docs
 * don't document an account-switch pattern either). So rather than attempting something that
 * isn't really supported, just tell the user how to do it themselves. Office Add-ins don't
 * support window.alert() (Office intercepts it and throws) -- toggle an inline panel instead.
 */
function toggleSwitchAccountInfo() {
  const panel = document.getElementById("switch-account-info");
  panel.style.display = panel.style.display === "none" ? "block" : "none";
}

// ===================== EXCEL =====================

async function connectExcelData() {
  try {
    const { columns, rows, fileName, sheetName } = await Excel.run(async (context) => {
      const selection = context.workbook.getSelectedRange();
      selection.load(["rowCount", "columnCount"]);
      const activeSheet = context.workbook.worksheets.getActiveWorksheet();
      activeSheet.load("name");
      await context.sync();

      const range = selection.rowCount > 1 || selection.columnCount > 1 ? selection : activeSheet.getUsedRange();
      // .text (not .values) -- the displayed text of each cell, respecting its Excel number
      // format (currency, dates, percentages, thousands separators, ...). .values would give
      // the raw underlying number instead (e.g. 1234.56 for a cell showing "$1,234.56"), which
      // is what merged into Word before this and dropped the formatting.
      range.load("text");
      await context.sync();

      const values = range.text;
      if (values.length < 2) {
        throw new Error("No data found. Select a range with a header row plus data, or use a sheet that has data.");
      }
      const headers = values[0].map((h) => h.trim());
      const dataRows = values.slice(1).map((row) =>
        Object.fromEntries(headers.map((h, i) => [h, row[i] === undefined ? "" : row[i]]))
      );

      const rawUrl = Office.context.document.url || "";
      const fileName = rawUrl.split(/[\\/]/).pop() || "Untitled workbook";

      return { columns: headers, rows: dataRows, fileName, sheetName: activeSheet.name };
    });

    canonicalizeEmailColumn(columns, rows);

    if (!columns.includes("Email")) {
      log('Your data must include a column named "Email" (any capitalization).', "error");
      return;
    }

    const validationErrors = validateRows(columns, rows, readValidationOptions());
    if (validationErrors.length > 0) {
      validationErrors.forEach((message) => log(message, "error"));
      return;
    }

    const response = await authFetch("/api/sessions", {
      method: "POST",
      body: JSON.stringify({ columns, rows, fileName, sheetName }),
    });
    if (!response.ok) throw new Error(`Backend returned ${response.status}`);
    await response.json();

    toggleValidationChecklist(false); // collapse now that we're past validation -- keeps the summary below in view
    document.getElementById("excel-result").style.display = "block";
    document.getElementById("excel-row-count").textContent = String(rows.length);

    const chipRow = document.getElementById("excel-columns");
    chipRow.innerHTML = "";
    columns.forEach((col) => {
      const chip = document.createElement("span");
      chip.className = col === "Email" ? "chip required" : "chip";
      chip.textContent = col;
      chipRow.appendChild(chip);
    });

    log(`Connected ${fileName} / ${sheetName} — ${rows.length} rows.`);
  } catch (error) {
    console.error(error);
    log(`Connect failed: ${error.message}`, "error");
  }
}

// ===================== WORD =====================

async function loadSessionList() {
  try {
    const response = await authFetch("/api/sessions");
    if (!response.ok) throw new Error(`Failed to list sessions (${response.status})`);
    const { sessions } = await response.json();
    sessionList = sessions;

    const picker = document.getElementById("session-picker");
    const previousSelection = currentSessionId;
    picker.innerHTML = '<option value="">-- choose a connected file --</option>';
    sessions.forEach((s) => {
      const opt = document.createElement("option");
      opt.value = s.id;
      opt.textContent = `${s.fileName || "Untitled"} — ${s.sheetName || ""} — ${s.rowCount} rows — ${timeAgo(s.createdAt)}`;
      picker.appendChild(opt);
    });

    // Keep whatever was selected before the refresh, and re-render tag buttons in case
    // the underlying data (e.g. columns) changed in Excel since it was first picked.
    const stillExists = previousSelection && sessions.some((s) => s.id === previousSelection);
    if (stillExists) {
      picker.value = previousSelection;
      applySession(sessionList.find((s) => s.id === previousSelection));
    }
  } catch (error) {
    console.error(error);
    log(`Could not load sessions: ${error.message}`, "error");
  }
}

function goToStep(step) {
  const cards = {
    1: document.getElementById("step1-card"),
    2: document.getElementById("step2-doc-card"),
    3: document.getElementById("step3-compose-card"),
    4: document.getElementById("step4-card"),
  };
  Object.entries(cards).forEach(([num, el]) => {
    const n = Number(num);
    el.style.display = n === step ? "block" : "none";
    el.classList.remove("active", "complete");
    if (n < step) el.classList.add("complete");
    if (n === step) el.classList.add("active");
  });
  // Which box a tag lands in is inferred from the step itself instead of a manual toggle: the
  // document step only ever targets the Word document; the compose step defaults to Subject
  // (the first field) and then follows focus from there (see the listeners in Office.onReady).
  if (step === 2) setInsertTarget("document");
  if (step === 3) setInsertTarget("subject");
  // Recomputed every time, not just once -- if the user goes back and changes the subject,
  // format, or archive folder, the summary shouldn't show stale values when they return.
  if (step === 4) renderSendSummary();
}

/** Step 4's recap box -- who it's sending as, how many recipients, and what's about to
 *  happen, so "Send emails" isn't a leap of faith. Async (fetches quota usage), but callers
 *  don't need to await it -- it only ever updates the DOM. */
async function renderSendSummary() {
  document.getElementById("summary-account").textContent = document.getElementById("account-name").textContent;

  const session = sessionList.find((s) => s.id === currentSessionId);
  const ccCount = parseEmailList(document.getElementById("email-cc").value).length;
  const bccCount = parseEmailList(document.getElementById("email-bcc").value).length;
  // Each email carries the same Cc/Bcc list on top of its one To address, so total recipients
  // scales with the batch size, not just the Cc/Bcc box contents.
  const batchRecipients = session ? session.rowCount * (1 + ccCount + bccCount) : 0;

  document.getElementById("summary-emails").textContent = session ? String(session.rowCount) : "—";
  document.getElementById("summary-recipients").textContent = session ? String(batchRecipients) : "—";

  const formatSelect = document.getElementById("output-format");
  const extension = formatSelect.options[formatSelect.selectedIndex].text; // ".pdf" or ".docx"
  const filenamePattern = document.getElementById("filename-input").value.trim();
  document.getElementById("summary-file").textContent = filenamePattern
    ? `${filenamePattern}${extension}`
    : `(untitled)${extension}`;

  const subject = document.getElementById("email-subject").value.trim();
  document.getElementById("summary-subject").textContent = subject || "(empty)";

  const archiveFolder = document.getElementById("archive-folder").value.trim();
  document.getElementById("summary-archive-row").style.display = archiveFolder ? "flex" : "none";
  document.getElementById("summary-archive").textContent = archiveFolder;

  await refreshQuota(session, batchRecipients);
}

/** Fetches recipient usage against Microsoft's rolling 10,000-recipients/24h mailbox limit and,
 *  only if this batch wouldn't fit in what's left, shows a blocking banner and disables Send.
 *  Silent otherwise -- no need to surface usage the user doesn't have to act on. This is UX only;
 *  the server enforces the real limit independently in /run (see the 429 handling in runBatch()
 *  below), so a quota-check hiccup here fails open rather than blocking a send that's actually fine. */
async function refreshQuota(session, batchRecipients) {
  const banner = document.getElementById("quota-blocked-banner");
  const runButton = document.getElementById("run-batch");

  if (!session) {
    banner.style.display = "none";
    return;
  }

  try {
    const response = await authFetch("/api/quota");
    if (!response.ok) throw new Error(`Failed to load quota (${response.status})`);
    const { recipients } = await response.json();
    const { exceeds, message } = evaluateRecipientQuota(batchRecipients, recipients.remaining);

    if (exceeds) {
      banner.textContent = message;
      banner.style.display = "block";
      runButton.disabled = true;
    } else {
      banner.style.display = "none";
      runButton.disabled = false;
    }
  } catch (error) {
    console.error(error);
    banner.style.display = "none";
  }
}

function switchTab(tab) {
  document.querySelectorAll("#word-tab-bar .tab-btn").forEach((btn) => {
    btn.classList.toggle("on", btn.dataset.tab === tab);
  });
  document.getElementById("word-actions-panel").style.display = tab === "actions" ? "block" : "none";
  document.getElementById("report-section").style.display = tab === "report" ? "block" : "none";
  if (tab === "report") loadRunSummaries(true);
}

function onSessionPicked(event) {
  const id = event.target.value;
  if (!id) {
    currentSessionId = null;
    document.getElementById("step1-next").disabled = true;
    return;
  }
  applySession(sessionList.find((s) => s.id === id));
}

function applySession(session) {
  currentSessionId = session.id;
  document.getElementById("step1-next").disabled = false;

  // Two separate tag grids -- one per step -- so each stays in sync with whichever insertion
  // target that step implies (see goToStep()), rather than one grid shared across both.
  populateTagGrid("tag-search-doc", "tag-buttons-doc", session.columns);
  populateTagGrid("tag-search-compose", "tag-buttons-compose", session.columns);

  log(`Loaded ${session.fileName} (${session.rowCount} rows).`);
}

function populateTagGrid(searchId, gridId, columns) {
  document.getElementById(searchId).value = "";

  const container = document.getElementById(gridId);
  container.innerHTML = "";
  columns.forEach((col) => {
    const btn = document.createElement("button");
    btn.className = col === "Email" ? "tag-pill required" : "tag-pill";
    btn.textContent = `{{${col}}}`;
    // Prevent the button from stealing focus away from subject/body inputs before the click fires.
    btn.addEventListener("mousedown", (e) => e.preventDefault());
    btn.addEventListener("click", () => insertTag(col));
    container.appendChild(btn);
  });
}

function filterTagPills(searchInput, gridId) {
  const query = searchInput.value.toLowerCase();
  document.querySelectorAll(`#${gridId} .tag-pill`).forEach((pill) => {
    pill.style.display = pill.textContent.toLowerCase().includes(query) ? "" : "none";
  });
}

function insertAtCursor(el, text) {
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  el.value = el.value.slice(0, start) + text + el.value.slice(end);
  const newPos = start + text.length;
  el.setSelectionRange(newPos, newPos);
  el.focus();
  // Setting .value directly doesn't fire a native "input" event the way typing does --
  // dispatch one so listeners (e.g. updateComposeNextState) react the same either way, instead
  // of only reacting when the user also types something by hand.
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

/** Same idea as insertAtCursor, for the contenteditable body box -- inserts at the current
 *  selection if it's inside the editor, otherwise appends at the end (e.g. a tag pill clicked
 *  before the editor was ever focused). */
function insertAtEditorCursor(editor, text) {
  editor.focus();
  const selection = window.getSelection();
  let range;
  if (selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
    range = selection.getRangeAt(0);
  } else {
    range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
  }
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.setEndAfter(node);
  selection.removeAllRanges();
  selection.addRange(range);
  // Same reasoning as insertAtCursor -- a programmatic DOM insert doesn't fire "input" on its
  // own, so dispatch one so the existing listener updates the compose step's Next button either way.
  editor.dispatchEvent(new Event("input", { bubbles: true }));
}

async function insertTag(columnName) {
  const tag = `{{${columnName}}}`;

  if (insertTarget === "subject") {
    insertAtCursor(document.getElementById("email-subject"), tag);
    return;
  }
  if (insertTarget === "body") {
    insertAtEditorCursor(document.getElementById("email-body-editor"), tag);
    return;
  }
  if (insertTarget === "filename") {
    insertAtCursor(document.getElementById("filename-input"), tag);
    return;
  }

  try {
    await Word.run(async (context) => {
      const range = context.document.getSelection();
      range.insertText(tag, Word.InsertLocation.replace);
      await context.sync();
    });
  } catch (error) {
    console.error(error);
    log(`Insert failed: ${error.message}`, "error");
  }
}

// ===================== EMAIL BODY RICH TEXT =====================
// A small Gmail-style toolbar (font, bold, italic, underline) over the body's contenteditable
// box, via document.execCommand -- deprecated in the web-standards sense, but still reliable
// in Chromium/WebView2 (what Office task panes actually run on) and the simplest way to get
// basic formatting without pulling in a full editor library for four buttons.

let savedBodyRange = null; // needed because clicking the font <select> steals focus/selection
// from the editor before its "change" event fires -- save it beforehand, restore just before
// applying. Bold/italic/underline buttons avoid this entirely via mousedown preventDefault.

function saveBodySelection() {
  const editor = document.getElementById("email-body-editor");
  const selection = window.getSelection();
  if (selection.rangeCount > 0 && editor.contains(selection.anchorNode)) {
    savedBodyRange = selection.getRangeAt(0).cloneRange();
  }
}

function restoreBodySelection() {
  if (!savedBodyRange) return;
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(savedBodyRange);
}

function updateRteToolbarState() {
  const editor = document.getElementById("email-body-editor");
  const selection = window.getSelection();
  if (!selection.anchorNode || !editor.contains(selection.anchorNode)) return;
  document.getElementById("body-bold").classList.toggle("on", document.queryCommandState("bold"));
  document.getElementById("body-italic").classList.toggle("on", document.queryCommandState("italic"));
  document.getElementById("body-underline").classList.toggle("on", document.queryCommandState("underline"));
}

function setupRichTextToolbar() {
  const editor = document.getElementById("email-body-editor");
  editor.addEventListener("keyup", saveBodySelection);
  editor.addEventListener("mouseup", saveBodySelection);
  document.addEventListener("selectionchange", updateRteToolbarState);

  function applyCommand(command) {
    editor.focus();
    document.execCommand(command);
    updateRteToolbarState();
  }

  const commands = { "body-bold": "bold", "body-italic": "italic", "body-underline": "underline" };
  Object.entries(commands).forEach(([id, command]) => {
    const btn = document.getElementById(id);
    btn.addEventListener("mousedown", (e) => e.preventDefault()); // keep the editor's selection intact
    btn.addEventListener("click", () => applyCommand(command));
  });

  // Plain contenteditable normally gets Ctrl/Cmd+B/I/U for free from the browser, but since
  // this task pane runs inside Word/Excel's own window, those same shortcuts are Office's own
  // document-wide accelerators and can get intercepted by the host before ever reaching the
  // editor. Handle them ourselves so they work reliably regardless of the host's behavior.
  const shortcutKeys = { b: "bold", i: "italic", u: "underline" };
  editor.addEventListener("keydown", (e) => {
    const command = (e.ctrlKey || e.metaKey) && !e.altKey ? shortcutKeys[e.key.toLowerCase()] : undefined;
    if (!command) return;
    e.preventDefault();
    applyCommand(command);
  });

  document.getElementById("body-font").addEventListener("change", (e) => {
    editor.focus();
    restoreBodySelection();
    if (e.target.value) document.execCommand("fontName", false, e.target.value);
  });
}

function getDocumentAsBase64() {
  return new Promise((resolve, reject) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: 65536 }, (fileResult) => {
      if (fileResult.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(fileResult.error.message));
        return;
      }
      const file = fileResult.value;
      const sliceCount = file.sliceCount;
      const slices = [];
      let received = 0;

      function getSlice(index) {
        file.getSliceAsync(index, (sliceResult) => {
          if (sliceResult.status !== Office.AsyncResultStatus.Succeeded) {
            file.closeAsync();
            reject(new Error(sliceResult.error.message));
            return;
          }
          slices[index] = sliceResult.value.data;
          received++;
          if (received === sliceCount) {
            file.closeAsync();
            const totalLength = slices.reduce((sum, s) => sum + s.length, 0);
            const merged = new Uint8Array(totalLength);
            let offset = 0;
            for (const slice of slices) {
              merged.set(slice, offset);
              offset += slice.length;
            }
            resolve(uint8ArrayToBase64(merged));
          } else {
            getSlice(index + 1);
          }
        });
      }

      if (sliceCount === 0) {
        file.closeAsync();
        resolve("");
      } else {
        getSlice(0);
      }
    });
  });
}

function uint8ArrayToBase64(bytes) {
  const chunkSize = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

async function previewRow() {
  if (!currentSessionId) {
    log("Pick the source file first.", "error");
    return;
  }
  const ccInput = document.getElementById("email-cc").value;
  const bccInput = document.getElementById("email-bcc").value;
  const badAddresses = [...invalidEmails(ccInput), ...invalidEmails(bccInput)];
  if (badAddresses.length > 0) {
    log(`Cc/Bcc has invalid address(es): ${badAddresses.join(", ")}.`, "error");
    return;
  }

  try {
    log("Building preview for row 1...");
    const templateBase64 = await getDocumentAsBase64();
    const subject = document.getElementById("email-subject").value;
    const body = document.getElementById("email-body-editor").innerHTML;
    const fileName = document.getElementById("filename-input").value;
    const cc = parseEmailList(ccInput);
    const bcc = parseEmailList(bccInput);

    const response = await authFetch(`/api/sessions/${currentSessionId}/preview`, {
      method: "POST",
      body: JSON.stringify({ templateBase64, subject, body, fileName, cc, bcc }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Preview failed (${response.status})`);
    }
    const { previewId, subject: renderedSubject, toEmail, fileName: renderedFileName } = await response.json();

    const dialogUrl =
      `${window.location.origin}/preview.html?previewId=${encodeURIComponent(previewId)}` +
      `&backendUrl=${encodeURIComponent(backendUrl)}` +
      `&subject=${encodeURIComponent(renderedSubject)}` +
      `&toEmail=${encodeURIComponent(toEmail)}` +
      `&fileName=${encodeURIComponent(renderedFileName)}` +
      `&cc=${encodeURIComponent(cc.join(", "))}` +
      `&bcc=${encodeURIComponent(bcc.join(", "))}`;

    Office.context.ui.displayDialogAsync(dialogUrl, { height: 80, width: 60 }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        log(`Could not open preview dialog: ${result.error.message}`, "error");
      }
    });
  } catch (error) {
    console.error(error);
    log(`Preview failed: ${error.message}`, "error");
  }
}

async function runBatch() {
  if (!currentSessionId) {
    log("Pick the source file first.", "error");
    return;
  }
  const session = sessionList.find((s) => s.id === currentSessionId);
  const rowCount = session ? session.rowCount : 0;
  if (rowCount === 0) {
    log("This session has no rows to send.", "error");
    return;
  }

  const ccInput = document.getElementById("email-cc").value;
  const bccInput = document.getElementById("email-bcc").value;
  const badAddresses = [...invalidEmails(ccInput), ...invalidEmails(bccInput)];
  if (badAddresses.length > 0) {
    log(`Cc/Bcc has invalid address(es): ${badAddresses.join(", ")}.`, "error");
    return;
  }
  const cc = parseEmailList(ccInput);
  const bcc = parseEmailList(bccInput);

  // Whole-batch preflight: re-check fresh (Step 4's readout could be stale by now) before
  // sending anything at all, so an over-limit batch is blocked outright rather than partially
  // sent up to whatever chunk happens to tip it over. /run's own per-chunk check (below) is the
  // real, authoritative gate -- this is just the difference between failing fast and failing
  // partway through.
  const batchRecipients = rowCount * (1 + cc.length + bcc.length);
  try {
    const quotaResponse = await authFetch("/api/quota");
    if (quotaResponse.ok) {
      const { recipients } = await quotaResponse.json();
      const { exceeds, message } = evaluateRecipientQuota(batchRecipients, recipients.remaining);
      if (exceeds) {
        log(message, "error");
        return;
      }
    }
  } catch (error) {
    console.error(error);
    // Quota-check hiccup -- fall through to /run, whose own per-chunk check is authoritative.
  }

  const runButton = document.getElementById("run-batch");
  const previewButton = document.getElementById("preview-row");
  runButton.disabled = true;
  previewButton.disabled = true;

  try {
    log("Reading document as template...");
    const templateBase64 = await getDocumentAsBase64();
    const subject = document.getElementById("email-subject").value;
    const body = document.getElementById("email-body-editor").innerHTML;
    const fileName = document.getElementById("filename-input").value;
    const outputFormat = document.getElementById("output-format").value;
    const archiveFolder = document.getElementById("archive-folder").value.trim();

    const uploadResponse = await authFetch(`/api/sessions/${currentSessionId}/template`, {
      method: "POST",
      body: JSON.stringify({ templateBase64, subject, body, fileName, archiveFolder, outputFormat, cc, bcc }),
    });
    if (!uploadResponse.ok) throw new Error(`Template upload failed (${uploadResponse.status})`);

    if (archiveFolder) log(`Archiving is on: files will also be saved to OneDrive folder "${archiveFolder}".`);

    // Sent a few rows per request (rather than one call that processes the whole batch
    // server-side) so the client always knows how many are done and can show real progress --
    // a single all-in-one call has no way to report partway through. Chunked rather than
    // strictly one row per request to keep request count reasonable on large batches (500
    // rows = 100 requests, not 500) while progress is still fine-grained at any real batch size.
    const CHUNK_SIZE = 5;
    const runId = crypto.randomUUID();
    const scopes = archiveFolder ? [...graphScopes, ...oneDriveScopes] : graphScopes;
    const results = [];
    setRunProgress(0, rowCount);

    for (let start = 1; start <= rowCount; start += CHUNK_SIZE) {
      const end = Math.min(start + CHUNK_SIZE - 1, rowCount);
      const rowIndices = [];
      for (let i = start; i <= end; i++) rowIndices.push(i);

      try {
        const chunkResponse = await authFetch(`/api/sessions/${currentSessionId}/run`, {
          method: "POST",
          scopes,
          body: JSON.stringify({ rowIndices, runId }),
        });
        if (!chunkResponse.ok) {
          const err = await chunkResponse.json().catch(() => ({}));
          const httpError = new Error(err.error || `Rows ${start}-${end} failed (${chunkResponse.status})`);
          // Flagged so the catch below can stop the whole run instead of treating it like an
          // ordinary chunk failure -- the server rejected this chunk because sending it would
          // breach Microsoft's rolling recipient limit, so retrying later chunks would just fail
          // the same way.
          if (chunkResponse.status === 429 && err.code === "recipient_limit_exceeded") {
            httpError.recipientLimitExceeded = true;
          }
          throw httpError;
        }
        const { results: chunkResults } = await chunkResponse.json();
        chunkResults.forEach((result) => {
          results.push(result);
          if (result.status === "sent") {
            let line = `Row ${result.index}: sent to ${result.email}`;
            if (result.archived === true) line += " (archived to OneDrive)";
            if (result.archived === false) line += ` (archive failed: ${result.archiveError})`;
            log(line, "success");
          } else {
            log(`Row ${result.index} (${result.email}): FAILED - ${result.error}`, "error");
          }
        });
      } catch (error) {
        if (error.recipientLimitExceeded) {
          // Nothing in this chunk (or later ones) was attempted -- stop immediately instead of
          // logging the remaining rows as "failed", which they never were.
          const notAttempted = rowCount - results.length;
          log(`${error.message} ${notAttempted} row(s) were not attempted.`, "error");
          break;
        }
        // A transport-level failure for the whole chunk (network blip, backend hiccup) --
        // record every row in it as failed locally rather than aborting the rest of what
        // could be a large batch. These rows won't have their own audit entry server-side
        // (the request never got a response), but the run still finishes and gets a summary
        // card instead of silently disappearing.
        console.error(error);
        log(`Rows ${start}-${end}: request failed - ${error.message}`, "error");
        for (let i = start; i <= end; i++) {
          results.push({ index: i, email: "", status: "failed", error: error.message, timestamp: new Date().toISOString() });
        }
      }

      setRunProgress(end, rowCount);

      // No separate "record the summary" call needed here -- /api/sessions/:id/run updates the
      // run's summary itself, from this chunk's results, before responding. So the summary is
      // exactly as durable as the rows it's counting: if the task pane closes or the connection
      // drops right after this chunk, whatever was sent so far still has an accurate card in
      // the Report tab, with nothing left for the client to fail to send afterward.

      // A small pause between chunks -- cheap first line of defense against tripping Graph's
      // sendMail throttling in the first place, on top of the server-side Retry-After handling
      // for when it happens anyway. Skipped after the last chunk, nothing left to wait for.
      if (end < rowCount) await sleep(300);
    }

    log(`Batch complete: ${results.filter((r) => r.status === "sent").length}/${results.length} sent.`);
    switchTab("report"); // triggers a fresh audit fetch, which will include this run's rows
  } catch (error) {
    console.error(error);
    log(`Sending failed: ${error.message}`, "error");
  } finally {
    runButton.disabled = false;
    previewButton.disabled = false;
    hideRunProgress();
    // Refreshes the "Last 24h" readout and re-applies the quota-based Send gate now that this
    // run's sends (if any) have changed how much headroom is left.
    renderSendSummary();
  }
}

/**
 * Report tab: a paginated view over batch summaries (across all sessions, most recent first)
 * -- one card per /run or /relaunch call ("7 sent, 3 failed"), not a flat per-row list. Each
 * card fetches its own row-level detail on demand, for download or relaunch.
 */
async function loadRunSummaries(reset) {
  if (runsLoading) return;
  runsLoading = true;
  const loadMoreBtn = document.getElementById("audit-load-more");
  const originalLabel = loadMoreBtn.textContent;
  loadMoreBtn.disabled = true;
  loadMoreBtn.textContent = "Loading...";

  try {
    if (reset) {
      runsCursor = "";
      runSummaries = [];
    }
    const params = new URLSearchParams({ limit: "5" });
    if (runsCursor) params.set("cursor", runsCursor);

    const response = await authFetch(`/api/audit/runs?${params.toString()}`);
    if (!response.ok) throw new Error(`Failed to load report (${response.status})`);
    const { summaries, nextCursor } = await response.json();

    runSummaries = reset ? summaries : [...runSummaries, ...summaries];
    runsCursor = nextCursor || "";
    renderRunSummaries(summaries, { append: !reset });
    loadMoreBtn.style.display = runsCursor ? "block" : "none";
  } catch (error) {
    console.error(error);
    log(`Couldn't load report: ${error.message}`, "error");
  } finally {
    runsLoading = false;
    loadMoreBtn.disabled = false;
    loadMoreBtn.textContent = originalLabel;
  }
}

function renderRunSummaries(summaries, { append }) {
  const list = document.getElementById("report-list");
  if (!append) list.innerHTML = "";

  if (!append && summaries.length === 0) {
    list.innerHTML = '<p class="field-caption">No sends yet -- run a batch to see it here.</p>';
    return;
  }

  summaries.forEach((s) => {
    const card = document.createElement("div");
    card.className = "report-item";

    const header = document.createElement("div");
    header.className = "report-item-header";
    header.innerHTML = `<span class="report-item-email">${s.timestamp ? new Date(s.timestamp).toLocaleString() : ""}</span>`;
    const downloadBtn = document.createElement("button");
    downloadBtn.className = "icon-btn-subtle";
    downloadBtn.title = "Download Excel report";
    downloadBtn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M8 2V10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '<path d="M4.5 7L8 10.5L11.5 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M2.5 13H13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      "</svg>";
    downloadBtn.onclick = () => downloadRunReport(s.runId, downloadBtn);
    header.appendChild(downloadBtn);
    card.appendChild(header);

    const countsRow = document.createElement("div");
    countsRow.className = "report-row";
    countsRow.innerHTML =
      // fileType is missing on run summaries recorded before this field existed -- omit the
      // suffix rather than showing "- undefined" for those older cards.
      `<span>${s.total} email${s.total === 1 ? "" : "s"}${s.fileType ? ` - ${s.fileType}` : ""}</span>` +
      `<span class="report-counts">` +
      `<span class="report-icon ok" title="Sent">&#10003; ${s.sentCount}</span>` +
      (s.failedCount > 0 ? `<span class="report-icon fail" title="Failed">&#10007; ${s.failedCount}</span>` : "") +
      `</span>`;
    card.appendChild(countsRow);

    if (s.failedCount > 0) {
      const actions = document.createElement("div");
      actions.className = "btn-row";
      actions.style.marginTop = "8px";

      const relaunchBtn = document.createElement("button");
      relaunchBtn.className = "btn";
      relaunchBtn.textContent = "Relaunch failed";
      relaunchBtn.onclick = () => relaunchRun(s.runId, relaunchBtn);
      actions.appendChild(relaunchBtn);

      card.appendChild(actions);
    }

    list.appendChild(card);
  });
}

/** Fetches every row-level record for one run (paginating internally -- a run can exceed one
 *  page) and exports it as an Excel file, scoped to just that batch. */
async function downloadRunReport(runId, button) {
  // innerHTML, not textContent -- this button holds an SVG icon (empty textContent), so
  // saving/restoring via textContent would silently wipe it out after the first use.
  const originalContent = button.innerHTML;
  button.disabled = true;
  button.textContent = "…";

  try {
    let rows = [];
    let cursor = "";
    do {
      const params = new URLSearchParams({ runId, limit: "50" });
      if (cursor) params.set("cursor", cursor);
      const response = await authFetch(`/api/audit?${params.toString()}`);
      if (!response.ok) throw new Error(`Failed to load run detail (${response.status})`);
      const { entries, nextCursor } = await response.json();
      rows = rows.concat(entries);
      cursor = nextCursor || "";
    } while (cursor);

    if (rows.length === 0) {
      log("Nothing to download for this batch.", "error");
      return;
    }

    const sheetRows = rows.map((r) => ({
      Row: r.rowIndex,
      Recipient: r.recipientEmail,
      Cc: (r.cc || []).join(", "),
      Bcc: (r.bcc || []).join(", "),
      Sender: r.sender || "",
      "Email subject": r.subject || "",
      "File name": r.fileName || "",
      "File type": r.fileType || "",
      Status: r.status,
      Time: r.timestamp ? new Date(r.timestamp).toLocaleString() : "",
      Error: r.error || "",
      Archived: r.archived === undefined ? "" : r.archived ? "Yes" : "No",
      "Archive location": r.archiveLocation || "",
      "Archive error": r.archiveError || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    XLSX.writeFile(workbook, `send-report-${timestamp}.xlsx`);
    log("Report downloaded.");
  } catch (error) {
    console.error(error);
    log(`Couldn't download report: ${error.message}`, "error");
  } finally {
    button.disabled = false;
    button.innerHTML = originalContent;
  }
}

/**
 * Re-sends just the rows that failed in this run. Requests the OneDrive scope too since the
 * card doesn't know upfront whether the original session had archiving on -- worst case is an
 * unneeded consent prompt, not a broken relaunch.
 */
async function relaunchRun(runId, button) {
  const originalLabel = button.textContent;
  button.disabled = true;
  button.textContent = "Relaunching...";

  try {
    const response = await authFetch(`/api/audit/runs/${runId}/relaunch`, {
      method: "POST",
      scopes: [...graphScopes, ...oneDriveScopes],
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || `Relaunch failed (${response.status})`);

    const sentCount = body.results.filter((r) => r.status === "sent").length;
    log(
      `Relaunch complete: ${sentCount}/${body.results.length} sent.`,
      sentCount === body.results.length ? "success" : "error"
    );
    await loadRunSummaries(true); // refresh so the new relaunch batch shows up as its own card
  } catch (error) {
    console.error(error);
    log(`Relaunch failed: ${error.message}`, "error");
  } finally {
    button.disabled = false;
    button.textContent = originalLabel;
  }
}
